import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, MountainSnow, Route as RouteIcon, Star, X } from "lucide-react";
import { fetchLandmark, fetchPeak } from "../api/features";
import { fetchTrail } from "../api/trails";
import { landmarkCategoryIcon, landmarkCategoryLabel } from "../lib/landmark-icons";

// Floating summary card for a collected peak/landmark/trail on the public
// share surfaces (activity share page, showcase). Pulls key facts from the
// public detail endpoints (they work logged out) and links through to the
// full detail page. Docks across the top on narrow screens.

export type SelectedFeature = { kind: "peak" | "landmark" | "trail"; slug: string; name: string };

function formatKm(km: number): string {
  return km >= 100 ? `${Math.round(km)} km` : `${km.toFixed(1)} km`;
}

export function FeatureSummaryCard({
  selected,
  onClose,
}: {
  selected: SelectedFeature;
  onClose: () => void;
}) {
  const { kind, slug } = selected;
  const peakQuery = useQuery({
    queryKey: ["feature-summary", "peak", slug],
    queryFn: () => fetchPeak(slug),
    enabled: kind === "peak",
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const landmarkQuery = useQuery({
    queryKey: ["feature-summary", "landmark", slug],
    queryFn: () => fetchLandmark(slug),
    enabled: kind === "landmark",
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const trailQuery = useQuery({
    queryKey: ["feature-summary", "trail", slug],
    queryFn: () => fetchTrail(slug),
    enabled: kind === "trail",
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const query = kind === "peak" ? peakQuery : kind === "landmark" ? landmarkQuery : trailQuery;
  const detailPath =
    kind === "peak" ? `/peaks/${slug}` : kind === "landmark" ? `/landmarks/${slug}` : `/trails/${slug}`;

  const label =
    kind === "peak"
      ? "Peak"
      : kind === "trail"
        ? "Trail"
        : landmarkCategoryLabel(landmarkQuery.data?.landmark.category);
  const icon =
    kind === "peak" ? (
      <MountainSnow size={14} />
    ) : kind === "trail" ? (
      <RouteIcon size={14} />
    ) : (
      landmarkCategoryIcon(landmarkQuery.data?.landmark.category, 14)
    );

  const place =
    kind === "peak"
      ? [peakQuery.data?.peak.region, peakQuery.data?.peak.country].filter(Boolean).join(", ")
      : kind === "trail"
        ? [trailQuery.data?.trail.region, trailQuery.data?.trail.country].filter(Boolean).join(", ")
        : [landmarkQuery.data?.landmark.region, landmarkQuery.data?.landmark.country]
            .filter(Boolean)
            .join(", ");
  const rating = query.data?.rating;
  const description =
    kind === "landmark"
      ? landmarkQuery.data?.landmark.description
      : kind === "trail"
        ? trailQuery.data?.trail.description
        : null;
  const trigPeak = kind === "landmark" ? landmarkQuery.data?.peak : null;
  const trail = kind === "trail" ? trailQuery.data?.trail : null;

  const subtitleBits: string[] = [];
  if (kind === "peak" && peakQuery.data) {
    subtitleBits.push(`${Math.round(peakQuery.data.peak.elevationM).toLocaleString("en-GB")} m`);
  }
  if (trail?.distanceKm != null) subtitleBits.push(formatKm(trail.distanceKm));
  if (trail?.elevationGainM != null)
    subtitleBits.push(`${Math.round(trail.elevationGainM).toLocaleString("en-GB")} m gain`);
  if (place) subtitleBits.push(place);

  return (
    <div
      className="atlas-glass feature-summary-popup"
      style={{
        position: "absolute",
        right: 16,
        top: 16,
        zIndex: 20,
        width: 320,
        maxWidth: "calc(100vw - 32px)",
        borderRadius: "var(--radius-xl)",
        boxShadow: "var(--shadow-lg)",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minWidth: 0,
      }}
    >
      <style>{`
        @media (max-width: 760px) {
          .feature-summary-popup { left: 8px !important; right: 8px !important; top: 8px !important;
            width: auto !important; max-width: none !important; max-height: 34vh !important; overflow-y: auto !important; }
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            letterSpacing: ".12em",
            textTransform: "uppercase",
            color: "var(--text-muted)",
          }}
        >
          {icon}
          {label}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            display: "grid",
            placeItems: "center",
            width: 26,
            height: 26,
            borderRadius: 999,
            border: "1px solid var(--border-subtle)",
            background: "var(--surface-raised)",
            color: "var(--text-secondary)",
            cursor: "pointer",
            padding: 0,
          }}
        >
          <X size={13} />
        </button>
      </div>

      <div>
        <h2
          style={{
            font: "var(--type-display)",
            fontSize: 18,
            lineHeight: 1.2,
            letterSpacing: "-.01em",
            color: "var(--text-primary)",
            margin: 0,
            overflowWrap: "anywhere",
          }}
        >
          {selected.name}
        </h2>
        {subtitleBits.length > 0 && (
          <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 4 }}>
            {subtitleBits.join(" · ")}
          </div>
        )}
      </div>

      {query.isLoading && <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Loading details…</div>}

      {description && (
        <p
          style={{
            margin: 0,
            fontSize: 12.5,
            lineHeight: 1.5,
            color: "var(--text-secondary)",
            display: "-webkit-box",
            WebkitLineClamp: 4,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {description}
        </p>
      )}

      {trail && trail.totalSections > 0 && (
        <div style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>
          {trail.totalSections} {trail.totalSections === 1 ? "section" : "sections"}
        </div>
      )}

      {trigPeak && (
        <div style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>
          Stands on{" "}
          <a
            href={`/peaks/${trigPeak.slug}`}
            style={{ color: "var(--text-primary)", fontWeight: 600, textDecoration: "none" }}
          >
            {trigPeak.name}
          </a>{" "}
          ({Math.round(trigPeak.elevationM).toLocaleString("en-GB")} m)
        </div>
      )}

      {rating && rating.count > 0 && rating.average != null && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12.5,
            color: "var(--text-secondary)",
          }}
        >
          <Star size={13} style={{ color: "var(--gold-400)", fill: "var(--gold-400)" }} />
          <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>{rating.average.toFixed(1)}</span>
          <span>
            · {rating.count} {rating.count === 1 ? "rating" : "ratings"}
          </span>
        </div>
      )}

      <a
        href={detailPath}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
          fontWeight: 600,
          color: "var(--gold-400)",
          textDecoration: "none",
          marginTop: 2,
        }}
      >
        View full details
        <ArrowRight size={14} />
      </a>
    </div>
  );
}
